import $ from 'jquery';

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

export default class WCCartParticipantsForm {
    constructor() {
        // cart update (update_cart AJAX) currently in flight
        this.updating = false;
        // checkParticipantsAjax request currently in flight
        this.validating = false;
        // user tried to go to checkout while an update was in flight -
        // resume the attempt once the update finishes
        this.checkoutIntent = false;

        if (!document.querySelector('.jsWCCartForm')) return;

        this.bindEvents();
        this.bindCheckoutButton();
        this.checkAllInputs();
    }

    // Direct binding on the button - some third-party handlers (blockUI,
    // WooCommerce core) can stop click propagation before it reaches the
    // document, so the delegated listener alone is not reliable here
    bindCheckoutButton() {
        const checkoutBtn = document.querySelector('.jsWcCartCheckoutBtn');

        if (!checkoutBtn || checkoutBtn.dataset.participantsBound) return;

        checkoutBtn.dataset.participantsBound = '1';
        checkoutBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleCheckoutClick(checkoutBtn);
        });
    }

    handleCheckoutClick(checkoutBtn) {
        if (this.updating) {
            this.checkoutIntent = true;
            return;
        }

        this.checkoutIntent = false;
        this.goToCheckout(checkoutBtn);
    }

    // All listeners are delegated to document so they survive the cart
    // form re-renders done by WooCommerce (updated_wc_div / fragments).
    bindEvents() {
        document.addEventListener('pointerdown', (e) => {
            // fires before the focused input blurs and triggers a cart
            // update, so the checkout intent is captured even when the
            // resulting blockUI overlay swallows the click event
            this.checkoutIntent = !!e.target.closest('.jsWcCartCheckoutBtn');
        });

        document.addEventListener('click', (e) => {
            const checkoutBtn = e.target.closest('.jsWcCartCheckoutBtn');
            if (checkoutBtn) {
                e.preventDefault();

                // fallback only - the directly bound listener handles this
                if (!checkoutBtn.dataset.participantsBound) {
                    this.handleCheckoutClick(checkoutBtn);
                }
                return;
            }

            const addBtn = e.target.closest('.jsWcParticipantsAdd');
            if (addBtn) {
                e.preventDefault();
                this.addNewUserItem(addBtn);
                return;
            }

            const removeBtn = e.target.closest('.jsWcParticipantsRemove');
            if (removeBtn) {
                e.preventDefault();
                this.removeUserItem(removeBtn);
            }
        });

        document.addEventListener('input', (e) => {
            if (!this.isParticipantInput(e.target)) return;

            e.target.classList.toggle('is-empty', !e.target.value.trim());
            this.checkAllInputs();
        });

        document.addEventListener('focusout', (e) => {
            if (!this.isParticipantInput(e.target)) return;

            this.updateUserItem(e.target);
            this.checkAllInputs();
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && this.isParticipantInput(e.target)) {
                e.preventDefault();
            }
        });

        // A page restored from the back/forward cache keeps the state it
        // was left with (validating stays true after a successful check) -
        // reset it, otherwise the checkout button is dead after "Back"
        window.addEventListener('pageshow', (e) => {
            if (!e.persisted) return;

            this.resetStateAfterPageRestore();
        });

        // WooCommerce re-renders the cart form on these jQuery events -
        // recompute the button state and resume an interrupted checkout
        $(document.body).on(
            'updated_wc_div updated_cart_totals wc_fragments_refreshed updated_shipping_method',
            () => {
                const resumeCheckout = this.updating && this.checkoutIntent;
                this.updating = false;
                this.bindCheckoutButton();
                this.checkAllInputs();

                if (resumeCheckout) {
                    this.checkoutIntent = false;
                    this.goToCheckout(
                        document.querySelector('.jsWcCartCheckoutBtn'),
                    );
                }
            },
        );
    }

    resetStateAfterPageRestore() {
        this.updating = false;
        this.validating = false;
        this.checkoutIntent = false;

        this.bindCheckoutButton();
        this.checkAllInputs();
    }

    isParticipantInput(target) {
        return (
            target instanceof Element &&
            target.classList.contains('jsWcParticipantsInput')
        );
    }

    isInputValid(input) {
        const value = input.value.trim();

        if (!value) return false;
        if (input.name.includes('userEmail')) return EMAIL_PATTERN.test(value);

        return true;
    }

    checkAllInputs() {
        const cartForm = document.querySelector('.jsWCCartForm');
        const checkoutBtn = document.querySelector('.jsWcCartCheckoutBtn');

        if (!cartForm || !checkoutBtn) return;

        const allInputs = [
            ...cartForm.querySelectorAll('.jsWcParticipantsInput'),
        ];
        const allValid = allInputs.every((input) => this.isInputValid(input));

        checkoutBtn.classList.toggle('is-disabled', !allValid);
    }

    addNewUserItem(addBtn) {
        const btnCartItemId = addBtn.dataset.product;

        if (!btnCartItemId) return;

        const selectedList = document.querySelector(
            `.jsWcParticipantsList[data-product="${CSS.escape(btnCartItemId)}"]`,
        );

        if (!selectedList) return;

        this.changeProductQuantity(btnCartItemId, 1);
        this.updateCart();
    }

    removeUserItem(removeBtn) {
        const btnUserId = removeBtn.dataset.userid;
        const btnProductId = removeBtn.dataset.product;

        if (!btnUserId || !btnProductId) return;

        const selectedItem = [
            ...document.querySelectorAll('.jsWcParticipantsAction'),
        ].filter(
            (item) =>
                item.dataset.product === btnProductId &&
                item.dataset.userid === btnUserId,
        )?.[0];

        if (!selectedItem) return;

        selectedItem.value = 'remove';

        this.changeProductQuantity(btnProductId, -1);
        this.updateCart();
    }

    changeProductQuantity(cartItemId, delta) {
        const filteredQty = [
            ...document.querySelectorAll('.jsQuantityInput'),
        ].filter((itemQty) => itemQty.name.includes(cartItemId))?.[0];

        if (!filteredQty) return;

        filteredQty.value = Number(filteredQty.value) + delta;
    }

    updateUserItem(input) {
        const item = input.closest('.jsWcParticipantsItem');

        if (!item) return;

        const itemInputs = [...item.querySelectorAll('.jsWcParticipantsInput')];

        if (!itemInputs.length) return;
        if (!itemInputs.every((itemInput) => this.isInputValid(itemInput)))
            return;

        const actionInput = item.querySelector('.jsWcParticipantsAction');

        if (!actionInput) return;

        actionInput.value = 'update';
        this.updateCart();
    }

    updateCart() {
        const wcCartUpdateCartBtn = document.querySelector(
            '.jsWCCartUpdateCartBtn',
        );

        if (!wcCartUpdateCartBtn) return;

        this.updating = true;
        wcCartUpdateCartBtn.setAttribute('aria-disabled', 'false');
        wcCartUpdateCartBtn.removeAttribute('disabled');
        wcCartUpdateCartBtn.click();
    }

    goToCheckout(button) {
        if (!button || this.validating) return;
        if (button.classList.contains('is-disabled')) return;

        const cartForm = document.querySelector('.jsWCCartForm');

        if (!cartForm) return;

        const myFormData = new FormData(cartForm);
        myFormData.append('security', window.global.security);

        this.validating = true;

        fetch(window.global.ajax_url + '?action=checkParticipantsAjax', {
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'same-origin',
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: myFormData,
        })
            .then((resp) => resp.json())
            .then((res) => {
                if (res.success && res?.data?.type === 'go_to_checkout') {
                    window.location.href =
                        button.dataset.checkoutUrl || button.href;
                } else if (!res.success && res?.data?.type === 'reload_cart') {
                    this.validating = false;
                    this.updateCart();
                } else {
                    this.validating = false;
                }
            })
            .catch((err) => {
                this.validating = false;
                console.error(err);
            });
    }
}
